import React, {
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import { AnimatePresence } from '@sopranodesign/framer-motion';
import {
  Flex,
  Modal,
  Table,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  PanelSlider,
  TableActions,
  useNotification,
  LoadingContainer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import {
  useQuery,
  useQueryClient
} from '@sopranodesign/pulse-hooks';
import {
  useAuth,
  PermissionFilter
} from '@sopranodesign/pulse-auth';

import {
  deleteSitemapUrl,
  recrawlSitemapUrl,
  saveSitemapUrlTags,
  createSitemapUrlTag,
  fetchSitemapUrlTags,
  fetchSitemapUrlAvailableTags
} from '../api';
import { StateLevel } from './';
import WarningMessageBody from './WarningMessageBody';
import { useTagManager } from './useTagManager';
import { useBulkSelection } from './useBulkSelection';
import { runBulkAction, describeBulkResult } from './bulkActionUtils';
import BulkActionsBar from './BulkActionsBar';
import TagModal from './TagModal';

const SitemapUrlExplore = ({
  sitemap: initialSitemap,
  handleHide,
  onOpenDeleteModal,
  onOpenRecrawlModal,
  onOpenTagsModal,
  loadingIds,
  errorIds
}) => {
  const { hasPermission } = useAuth();
  const canViewTag = hasPermission('view generative ai tag');
  const canEditTag = hasPermission('edit generative ai tag');
  const canEditResource = hasPermission('edit generative ai resource');
  const canBulkSelect = canEditResource || (canViewTag && canEditTag);

  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [shouldRefetch, setShouldRefetch] = useState(true);
  const [isVisible, setIsVisible] = useState(!!initialSitemap);

  const bulk = useBulkSelection();
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [isBulkTagsModalVisible, setIsBulkTagsModalVisible] = useState(false);

  const order = useOrderableTableReducer({ id: 'sitemapUrls' });
  const pagination = usePaginationReducer({ id: 'sitemapUrls' });

  const urlsQueryKey = [`generative-ai/sitemap/${initialSitemap?.id}/urls`, { ...pagination.params, ...order.params }];
  const refetchUrls = () => queryClient.invalidateQueries({ queryKey: [`generative-ai/sitemap/${initialSitemap?.id}/urls`] });

  const { data: availableTagsData } = useQuery({
    queryKey: ['generative-ai/sitemap/groups/available'],
    queryFn: fetchSitemapUrlAvailableTags,
    staleTime: 5 * 60 * 1000,
    enabled: canViewTag
  });

  const { tags, selectedTags, setSelectedTags, handleTagChange, setTags } = useTagManager({
    scope: 'generative_ai_sitemap_urls',
    createTagFn: createSitemapUrlTag
  });

  useEffect(() => {
    setIsVisible(!!initialSitemap);
  }, [initialSitemap]);

  const {
    data: localSitemapUrls,
    error,
    isLoading
  } = useQuery({
    queryKey: urlsQueryKey,
    refetchInterval: isVisible && shouldRefetch ? 5000 : false,
    enabled: isVisible && !!initialSitemap
  });

  useEffect(() => {
    if (!isVisible) {
      setShouldRefetch(false);
      return;
    }
    const pending = localSitemapUrls?.has_pending;
    setShouldRefetch(pending);
  }, [isVisible, localSitemapUrls]);

  if (!isVisible) {
    return null;
  }

  const meta = localSitemapUrls?.meta ?? {};
  const urls = localSitemapUrls?.data ?? [];
  const pageIds = urls.map(url => url.id);

  const findUrl = (id) => urls.find(url => url.id === id);

  const handleBulkDelete = () => {
    const eligibleIds = bulk.selectedIds.filter(id => {
      const url = findUrl(id);
      return url && !['Manually Deleted', 'Deleting'].includes(url.status);
    });
    if (eligibleIds.length === 0) {
      showError('None of the selected URLs can be deleted right now.');
      return;
    }
    setBulkModal({
      header: 'Confirm Delete',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to delete ${eligibleIds.length} selected URL${eligibleIds.length > 1 ? 's' : ''}?`}
          contentBody="All synced data will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Delete',
      handleConfirm: async () => {
        setBulkLoading(true);
        try {
          const result = await runBulkAction(eligibleIds, deleteSitemapUrl);
          if (result.succeeded.length > 0) {
            showSuccess(`${describeBulkResult(result, 'delete requested', 'URL')}.`);
          }
          if (result.failed.length > 0) {
            showError(`Failed to request delete for ${result.failed.length} URL${result.failed.length > 1 ? 's' : ''}.`);
          }
          bulk.clear();
        } finally {
          setBulkLoading(false);
          setBulkModal(null);
          refetchUrls();
        }
      }
    });
  };

  const handleBulkRecrawl = () => {
    const eligibleIds = bulk.selectedIds.filter(id => {
      const url = findUrl(id);
      return url && url.status !== 'Deleting';
    });
    if (eligibleIds.length === 0) {
      showError('None of the selected URLs can be recrawled right now.');
      return;
    }
    setBulkModal({
      header: 'Confirm Recrawl',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to recrawl ${eligibleIds.length} selected URL${eligibleIds.length > 1 ? 's' : ''}?`}
        />
      ),
      confirmText: 'Recrawl',
      handleConfirm: async () => {
        setBulkLoading(true);
        try {
          const result = await runBulkAction(eligibleIds, recrawlSitemapUrl);
          if (result.succeeded.length > 0) {
            showSuccess(`${describeBulkResult(result, 'recrawl requested', 'URL')}.`);
          }
          if (result.failed.length > 0) {
            showError(`Failed to request recrawl for ${result.failed.length} URL${result.failed.length > 1 ? 's' : ''}.`);
          }
          bulk.clear();
        } finally {
          setBulkLoading(false);
          setBulkModal(null);
          refetchUrls();
        }
      }
    });
  };

  const openBulkTagsModal = () => {
    const eligibleIds = bulk.selectedIds.filter(id => findUrl(id)?.status === 'Success');
    if (eligibleIds.length === 0) {
      showError('None of the selected URLs are ready to be tagged.');
      return;
    }
    const fetchedTags = availableTagsData?.data?.data ?? [];
    setTags(fetchedTags);
    setSelectedTags([]);
    setIsBulkTagsModalVisible(true);
  };

  const handleBulkTagConfirm = async () => {
    const eligibleIds = bulk.selectedIds.filter(id => findUrl(id)?.status === 'Success');
    setBulkLoading(true);
    try {
      const result = await runBulkAction(eligibleIds, async (id) => {
        const assignedResponse = await fetchSitemapUrlTags(id);
        const currentTagIds = (assignedResponse.data.data ?? []).map(tag => tag.id);
        const mergedTagIds = Array.from(new Set([...currentTagIds, ...selectedTags]));
        return saveSitemapUrlTags({ sitemapId: id, groupIds: mergedTagIds });
      });
      if (result.succeeded.length > 0) {
        showSuccess(`${describeBulkResult(result, 'tagged', 'URL')}.`);
      }
      if (result.failed.length > 0) {
        showError(`Failed to tag ${result.failed.length} URL${result.failed.length > 1 ? 's' : ''}.`);
      }
      bulk.clear();
      refetchUrls();
    } finally {
      setBulkLoading(false);
      setIsBulkTagsModalVisible(false);
      setSelectedTags([]);
    }
  };

  return (
    <AnimatePresence>
      <PanelSlider
        header="Explore Sitemap URLs"
        handleHide={() => {
          setIsVisible(false);
          handleHide();
        }}
      >
        <Flex col>
          {isLoading ? (
            <LoadingContainer />
          ) : error ? (
            <p style={{ color: 'red' }}>
              Failed to fetch sitemap URLs: {error.message || 'Unknown error occurred'}
            </p>
          ) : localSitemapUrls?.data?.length > 0 ? (
            <>
              <PermissionFilter can="edit generative ai resource">
                <BulkActionsBar
                  count={bulk.count}
                  onClear={bulk.clear}
                  actions={[
                    <PermissionFilter can="view generative ai tag" key="bulk-tag">
                      <PermissionFilter can="edit generative ai tag">
                        <Button
                          icon="tag"
                          variant="secondary"
                          onClick={openBulkTagsModal}
                        >
                          Tags
                        </Button>
                      </PermissionFilter>
                    </PermissionFilter>,
                    <Button
                      key="bulk-recrawl"
                      icon="download"
                      variant="secondary"
                      loading={bulkLoading}
                      onClick={handleBulkRecrawl}
                    >
                      Recrawl
                    </Button>,
                    <Button
                      key="bulk-delete"
                      variant="secondary"
                      icon="trash"
                      colour="danger"
                      loading={bulkLoading}
                      onClick={handleBulkDelete}
                    >
                      Delete
                    </Button>
                  ]}
                />
              </PermissionFilter>
            <Flex border xScroll>
              <Table>
                <TableHead>
                  <OrderableTableRow
                    rows={[
                      ...(canBulkSelect ? [{
                        label: (
                          <Checkbox
                            aria-label="Select all URLs"
                            checked={bulk.isAllSelected(pageIds)}
                            onChange={() => bulk.toggleAll(pageIds)}
                          />
                        )
                      }] : []),
                      { label: 'Url', sort: 'url' },
                      { label: 'Status', sort: 'status' },
                      { label: 'Crawled At', sort: 'updated_at' },
                      ...(canViewTag ? [{ label: 'Tags' }] : []),
                      ...(((canViewTag && canEditTag) || canEditResource) ? [null] : [])
                    ]}
                    {...order.props}
                  />
                </TableHead>
                <TableBody>
                  {localSitemapUrls.data.map(sitemap => (
                    <TableRow key={sitemap.id}>
                      {canBulkSelect && (
                        <TableCell>
                          <Checkbox
                            aria-label={`Select ${sitemap.url}`}
                            checked={bulk.isSelected(sitemap.id)}
                            onChange={() => bulk.toggle(sitemap.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <a href={sitemap.url} target="_blank" rel="noopener noreferrer">
                          {sitemap.url}
                        </a>
                      </TableCell>
                      <TableCell>
                        <Flex center>
                          <StateLevel level={sitemap.status} />
                          <span>{sitemap.status}</span>
                        </Flex>
                      </TableCell>
                      <TableCell>{dayjs(sitemap.updated_at).format('D MMM YYYY HH:mm')}</TableCell>
                      <PermissionFilter can="view generative ai tag">
                        <TableCell>
                          {sitemap.groups && sitemap.groups.length > 0
                            ? sitemap.groups.map(group => group.name).join(', ')
                            : 'No Tags'}
                        </TableCell>
                      </PermissionFilter>
                      {((canViewTag && canEditTag) || canEditResource) &&
                        <TableActions>
                          <PermissionFilter can="view generative ai tag">
                            <PermissionFilter can="edit generative ai tag">
                              {sitemap.status === 'Success' && (
                                <Button
                                  icon="tag"
                                  onClick={() => onOpenTagsModal(sitemap)}
                                >
                                  Tags
                                </Button>
                              )}
                            </PermissionFilter>
                          </PermissionFilter>
                          <PermissionFilter can="edit generative ai resource">
                            {!['Manually Deleted', 'Deleting'].includes(sitemap.status) && (
                              <Button
                                colour="danger"
                                icon="trash"
                                loading={loadingIds[sitemap.id]}
                                onClick={() => onOpenDeleteModal(sitemap)}
                              >
                                Delete
                              </Button>
                            )}
                            {!['Deleting'].includes(sitemap.status) && (
                              <Button
                                icon="download"
                                loading={loadingIds[sitemap.id]}
                                onClick={() => onOpenRecrawlModal(sitemap)}
                              >
                                {sitemap.status !== 'Manually Deleted' ? 'Recrawl' : 'Enable and Crawl'}
                              </Button>
                            )}
                          </PermissionFilter>
                          {errorIds[sitemap.id] && (
                            <p style={{ color: 'red', fontSize: '12px' }}>{errorIds[sitemap.id]}</p>
                          )}
                        </TableActions>
                      }
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Flex>
            </>
          ) : (
            <p>No URLs available for this sitemap.</p>
          )}

          {meta && <Pagination pagination={meta} {...pagination.props} />}

          <TagModal
            isVisible={isBulkTagsModalVisible}
            header={`Tag ${bulk.count} Sitemap URLs`}
            onCancel={() => {
              setIsBulkTagsModalVisible(false);
              setSelectedTags([]);
            }}
            onConfirm={handleBulkTagConfirm}
            tags={tags}
            selectedTags={selectedTags}
            handleTagChange={handleTagChange}
          />

          {bulkModal && (
            <Modal
              header={bulkModal.header}
              body={bulkModal.body}
              confirmText={bulkModal.confirmText}
              cancelText="Cancel"
              handleCancel={() => setBulkModal(null)}
              handleConfirm={bulkModal.handleConfirm}
              loading={bulkLoading}
            />
          )}
        </Flex>
      </PanelSlider>
    </AnimatePresence >
  );
};

SitemapUrlExplore.propTypes = {
  handleHide: PropTypes.func.isRequired,
  sitemap: PropTypes.object.isRequired,
  onOpenDeleteModal: PropTypes.func.isRequired,
  onOpenRecrawlModal: PropTypes.func.isRequired,
  onOpenTagsModal: PropTypes.func.isRequired,
  loadingIds: PropTypes.object.isRequired,
  errorIds: PropTypes.object.isRequired
};

export default SitemapUrlExplore;